import { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Megaphone, Plus, Loader2 } from 'lucide-react';
import { useCampaignStore } from '../store/campaignStore';
import { CampaignStatusChip } from '../components/campaigns';
import type { Campaign } from '../types/campaign';

function formatSchedule(campaign: Campaign): string {
  if (!campaign.sendAt) return 'Manual';
  return new Date(campaign.sendAt).toLocaleString();
}

function formatLastRun(campaign: Campaign): string {
  const run = campaign.runs?.[0];
  if (!run) return '—';
  const accepted = run.acceptedCount ?? 0;
  const suffix = run.kind === 'TEST' ? ' (test)' : '';
  return `${run.status} — ${accepted} accepted${suffix}`;
}

/**
 * Campaigns list — SyncJobsTab's table idiom (Tasks.tsx page shell). Row
 * click opens the composer for that campaign; "New campaign" starts a fresh
 * one at /campaigns/new.
 */
export const Campaigns = () => {
  const { campaigns, isLoading, error, fetchCampaigns } = useCampaignStore();
  const navigate = useNavigate();

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 bg-blast-100 dark:bg-blast-900/30 rounded-lg flex-shrink-0">
              <Megaphone className="h-6 w-6 text-blast-600 dark:text-blast-400" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Campaigns</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {campaigns.length} campaign{campaigns.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
            <Link to="/campaigns/templates" className="btn-secondary whitespace-nowrap">
              Templates
            </Link>
            <Link to="/campaigns/new" className="btn-primary whitespace-nowrap">
              <Plus className="h-4 w-4 mr-1.5" />
              New campaign
            </Link>
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <span className="text-red-800 dark:text-red-300">{error}</span>
          </div>
        )}

        {/* Loading state */}
        {isLoading && campaigns.length === 0 && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
          </div>
        )}

        {/* Empty state */}
        {!isLoading && campaigns.length === 0 && (
          <div className="text-center py-12 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
            <Megaphone className="h-16 w-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No campaigns yet</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">Build a guided campaign to reach coaches, team admins, or CRM contacts</p>
            <Link to="/campaigns/new" className="btn-primary">
              <Plus className="h-4 w-4 mr-2" />
              New campaign
            </Link>
          </div>
        )}

        {/* Campaigns table */}
        {campaigns.length > 0 && (
          <div className="card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700/50 text-xs uppercase text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Audience</th>
                  <th className="px-3 py-2 text-left">Template</th>
                  <th className="px-3 py-2 text-left">Schedule</th>
                  <th className="px-3 py-2 text-left">Last run</th>
                </tr>
              </thead>
              <tbody className="text-gray-900 dark:text-gray-100">
                {campaigns.map((campaign) => (
                  <tr
                    key={campaign.id}
                    className="border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                    onClick={() => navigate(`/campaigns/${campaign.id}`)}
                  >
                    <td className="px-3 py-2 font-medium whitespace-nowrap">{campaign.name}</td>
                    <td className="px-3 py-2">
                      <CampaignStatusChip status={campaign.status} />
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{campaign.list?.name ?? '—'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{campaign.template?.name ?? '—'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{formatSchedule(campaign)}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">{formatLastRun(campaign)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
